import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, IconButton, styled,
  Grid, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button, Tabs, Tab,
  TextField, Select, MenuItem, FormControl, InputLabel, Switch,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
  Pagination, Alert
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import ShieldIcon from '@mui/icons-material/Shield';
import SecurityIcon from '@mui/icons-material/Security';
import BlockIcon from '@mui/icons-material/Block';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import WrapTextIcon from '@mui/icons-material/WrapText';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SuperAdminLayout from './SuperAdminLayout';
import {
  getSecurityStats, getSecurityLogs, getRecentLogs,
  getLogDetail, getApiNames,
  getRateLimitRules, createRateLimitRule, toggleRateLimitRule, deleteRateLimitRule,
  getIpAccessRules, createIpAccessRule, toggleIpAccessRule, deleteIpAccessRule,
  listFileLogs, tailFileLog, getAuthFailedSummary,
  type SecurityStats, type ApiSecurityLog, type RateLimitRule, type IpAccessRule, type LogFilters,
  type FileLogFile, type AuthFailedSummary
} from '../../services/SecurityMonitorService';

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: 'center',
  borderRadius: 12,
  height: '100%',
}));

// ════════════════════════════════════════════════════════════════════════
// Log Type Helpers
// ════════════════════════════════════════════════════════════════════════

const LOG_TYPE_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default' | 'primary' | 'secondary' | 'success'> = {
  RATE_LIMIT: 'warning',
  IP_BLOCKED: 'error',
  AUTH_FAILED: 'secondary',
  EXCEPTION: 'error',
  SUSPICIOUS: 'warning',
};

const LOG_TYPE_LABELS: Record<string, string> = {
  RATE_LIMIT: 'Rate Limit',
  IP_BLOCKED: 'IP Engel',
  AUTH_FAILED: 'Auth Hata',
  EXCEPTION: 'Exception',
  SUSPICIOUS: 'Şüpheli',
};

// ════════════════════════════════════════════════════════════════════════
// LogViewer - Terminal görünümlü log içerik gösterici (reusable)
// ════════════════════════════════════════════════════════════════════════

interface LogViewerProps {
  lines: string[];
  loading: boolean;
  wrap: boolean;
  maxHeight: number | string;
  fullscreen?: boolean;
}

const LogViewer: React.FC<LogViewerProps> = ({ lines, loading, wrap, maxHeight, fullscreen }) => {
  return (
    <Box
      sx={{
        bgcolor: '#1e1e1e',
        color: '#e0e0e0',
        fontFamily: 'Consolas, "Courier New", monospace',
        fontSize: fullscreen ? '0.8rem' : '0.75rem',
        p: 2,
        borderRadius: fullscreen ? 0 : 1,
        maxHeight,
        height: fullscreen ? maxHeight : undefined,
        overflowX: 'auto',
        overflowY: 'auto',
        lineHeight: 1.5,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {lines.length === 0 ? (
        <Box sx={{ color: '#888' }}>
          {loading
            ? 'Yükleniyor...'
            : '"Oku" butonuna basın veya Auto-Refresh\'i açın.'}
        </Box>
      ) : (
        lines.map((line, idx) => {
          const isError = /\[(ERR|FTL)\]|Exception/i.test(line);
          const isWarn = /\[WRN\]/i.test(line);
          const isInfo = /\[INF\]/i.test(line);
          return (
            <Box
              key={idx}
              component="div"
              sx={{
                color: isError ? '#ff6b6b' : isWarn ? '#ffd166' : isInfo ? '#90caf9' : '#e0e0e0',
                whiteSpace: wrap ? 'pre-wrap' : 'pre',
                wordBreak: wrap ? 'break-all' : 'normal',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                display: 'flex',
                gap: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: '#555',
                  userSelect: 'none',
                  minWidth: '3.5em',
                  textAlign: 'right',
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </Box>
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                {line}
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════════════════════

const MiddlewareMonitor: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<SecurityStats | null>(null);

  // Rate Limit Rules
  const [rateLimitRules, setRateLimitRules] = useState<RateLimitRule[]>([]);
  const [showRateLimitDialog, setShowRateLimitDialog] = useState(false);
  const [newRateLimitRule, setNewRateLimitRule] = useState({ ruleName: '', scope: 'GLOBAL', companyId: '', endpointPattern: '', maxRequests: 100, windowSeconds: 60, burstLimit: 0, isActive: true, priority: 0 });

  // IP Access Rules
  const [ipAccessRules, setIpAccessRules] = useState<IpAccessRule[]>([]);
  const [showIpDialog, setShowIpDialog] = useState(false);
  const [newIpRule, setNewIpRule] = useState({ ipAddress: '', ipRangeEnd: '', ruleType: 'BLACKLIST', scope: 'GLOBAL', companyId: '', reason: '', isActive: true, isPermanent: false, expiresAt: null as string | null });

  // Logs
  const [logs, setLogs] = useState<ApiSecurityLog[]>([]);
  const [logFilters, setLogFilters] = useState<LogFilters>({ page: 1, pageSize: 50 });
  const [logTotalCount, setLogTotalCount] = useState(0);

  // Live logs
  const [recentLogs, setRecentLogs] = useState<ApiSecurityLog[]>([]);

  // API names (for filter dropdown)
  const [apiNames, setApiNames] = useState<string[]>([]);

  // Log detail modal
  const [logDetailOpen, setLogDetailOpen] = useState(false);
  const [logDetailLoading, setLogDetailLoading] = useState(false);
  const [logDetail, setLogDetail] = useState<ApiSecurityLog | null>(null);

  // File logs (Tab 4)
  const [fileLogsApi, setFileLogsApi] = useState<string>('gts_dotnetapi');
  const [fileLogsInstance, setFileLogsInstance] = useState<'instance_a' | 'instance_b'>('instance_a');
  const [fileLogsList, setFileLogsList] = useState<FileLogFile[]>([]);
  const [fileLogsSelectedFile, setFileLogsSelectedFile] = useState<string>('');
  const [fileLogsLines, setFileLogsLines] = useState<number>(200);
  const [fileLogsGrep, setFileLogsGrep] = useState<string>('');
  const [fileLogsContent, setFileLogsContent] = useState<string[]>([]);
  const [fileLogsLoading, setFileLogsLoading] = useState(false);
  const [fileLogsAutoRefresh, setFileLogsAutoRefresh] = useState(false);
  const [fileLogsError, setFileLogsError] = useState<string>('');
  const [fileLogsFullscreen, setFileLogsFullscreen] = useState(false);
  const [fileLogsWrap, setFileLogsWrap] = useState(false);

  // Tab 5: JWT devreye alma izlemesi (AUTH_FAILED ozeti)
  const [authFailedHours, setAuthFailedHours] = useState(1);
  const [authFailedSummary, setAuthFailedSummary] = useState<AuthFailedSummary | null>(null);
  const [authFailedLoading, setAuthFailedLoading] = useState(false);
  const [authFailedAutoRefresh, setAuthFailedAutoRefresh] = useState(false);

  // ════════════════════════════════════════════════════════════════════════
  // Data Loading
  // ════════════════════════════════════════════════════════════════════════

  const loadStats = useCallback(async () => {
    try {
      const data = await getSecurityStats();
      setStats(data);
    } catch (err) { console.error('Stats error:', err); }
  }, []);

  const loadRateLimitRules = useCallback(async () => {
    try { setRateLimitRules((await getRateLimitRules()) || []); } catch (err) { console.error('RateLimit rules error:', err); }
  }, []);

  const loadIpAccessRules = useCallback(async () => {
    try { setIpAccessRules((await getIpAccessRules()) || []); } catch (err) { console.error('IP rules error:', err); }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const data = await getSecurityLogs(logFilters);
      setLogs(data?.data || []);
      setLogTotalCount(data?.totalCount || 0);
    } catch (err) { console.error('Logs error:', err); }
  }, [logFilters]);

  const loadRecentLogs = useCallback(async () => {
    try { setRecentLogs((await getRecentLogs(50)) || []); } catch (err) { console.error('Recent logs error:', err); }
  }, []);

  const loadApiNames = useCallback(async () => {
    try { setApiNames((await getApiNames()) || []); } catch (err) { console.error('API names error:', err); }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadStats(), loadRateLimitRules(), loadIpAccessRules(), loadApiNames()]);
    setLoading(false);
  }, [loadStats, loadRateLimitRules, loadIpAccessRules, loadApiNames]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (activeTab === 2) loadLogs(); }, [activeTab, loadLogs]);
  useEffect(() => {
    if (activeTab !== 3) return;
    loadRecentLogs();
    const interval = setInterval(loadRecentLogs, 10000);
    return () => clearInterval(interval);
  }, [activeTab, loadRecentLogs]);

  // ════════════════════════════════════════════════════════════════════════
  // Log Detail Modal
  // ════════════════════════════════════════════════════════════════════════

  const openLogDetail = useCallback(async (id: number) => {
    setLogDetailOpen(true);
    setLogDetailLoading(true);
    setLogDetail(null);
    try {
      const detail = await getLogDetail(id);
      setLogDetail(detail);
    } catch (err) {
      console.error('Log detail error:', err);
    } finally {
      setLogDetailLoading(false);
    }
  }, []);

  // ════════════════════════════════════════════════════════════════════════
  // File Logs (Tab 4)
  // ════════════════════════════════════════════════════════════════════════

  const loadFileLogsList = useCallback(async () => {
    if (!fileLogsApi) return;
    try {
      const data = await listFileLogs(fileLogsApi);
      const files = (data?.files || []).filter(f => f.instance === fileLogsInstance);
      setFileLogsList(files);
      if (!fileLogsSelectedFile && files.length > 0) {
        setFileLogsSelectedFile(files[0].fileName);
      }
    } catch (err) {
      console.error('File logs list error:', err);
      setFileLogsList([]);
    }
  }, [fileLogsApi, fileLogsInstance, fileLogsSelectedFile]);

  const loadFileLogsTail = useCallback(async () => {
    if (!fileLogsApi) return;
    setFileLogsLoading(true);
    setFileLogsError('');
    try {
      const data = await tailFileLog({
        apiName: fileLogsApi,
        instance: fileLogsInstance,
        fileName: fileLogsSelectedFile || undefined,
        lines: fileLogsLines,
        grep: fileLogsGrep || undefined
      });
      if (data?.success) {
        setFileLogsContent(data.lines || []);
      } else {
        setFileLogsContent([]);
        setFileLogsError(data?.message || 'Dosya okunamadi');
      }
    } catch (err: any) {
      console.error('File logs tail error:', err);
      setFileLogsError(err?.message || 'Istek basarisiz');
      setFileLogsContent([]);
    } finally {
      setFileLogsLoading(false);
    }
  }, [fileLogsApi, fileLogsInstance, fileLogsSelectedFile, fileLogsLines, fileLogsGrep]);

  // Tab 4 acildiginda veya parametre degisince dosya listesini yenile
  useEffect(() => {
    if (activeTab === 4) {
      setFileLogsSelectedFile('');
      loadFileLogsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fileLogsApi, fileLogsInstance]);

  // Auto-refresh
  useEffect(() => {
    if (activeTab !== 4 || !fileLogsAutoRefresh) return;
    loadFileLogsTail();
    const interval = setInterval(loadFileLogsTail, 5000);
    return () => clearInterval(interval);
  }, [activeTab, fileLogsAutoRefresh, loadFileLogsTail]);

  // ════════════════════════════════════════════════════════════════════════
  // JWT Devreye Alma İzlemesi (Tab 5)
  // ════════════════════════════════════════════════════════════════════════

  const loadAuthFailedSummary = useCallback(async () => {
    setAuthFailedLoading(true);
    try {
      setAuthFailedSummary(await getAuthFailedSummary(authFailedHours));
    } catch (err) {
      console.error('Auth failed summary error:', err);
      setAuthFailedSummary(null);
    } finally {
      setAuthFailedLoading(false);
    }
  }, [authFailedHours]);

  useEffect(() => {
    if (activeTab !== 5) return;
    loadAuthFailedSummary();
  }, [activeTab, loadAuthFailedSummary]);

  // Bayrak açıldıktan sonraki ilk 15 dakika dakika bazında izleniyor; elle
  // yenilemek yerine 30 saniyelik otomatik yenileme bırakıyoruz.
  useEffect(() => {
    if (activeTab !== 5 || !authFailedAutoRefresh) return;
    const interval = setInterval(loadAuthFailedSummary, 30000);
    return () => clearInterval(interval);
  }, [activeTab, authFailedAutoRefresh, loadAuthFailedSummary]);

  // ════════════════════════════════════════════════════════════════════════
  // Rate Limit CRUD
  // ════════════════════════════════════════════════════════════════════════

  const handleCreateRateLimitRule = async () => {
    try {
      await createRateLimitRule({
        ...newRateLimitRule,
        companyId: newRateLimitRule.companyId || null,
        endpointPattern: newRateLimitRule.endpointPattern || null,
        burstLimit: newRateLimitRule.burstLimit || null,
      });
      setShowRateLimitDialog(false);
      setNewRateLimitRule({ ruleName: '', scope: 'GLOBAL', companyId: '', endpointPattern: '', maxRequests: 100, windowSeconds: 60, burstLimit: 0, isActive: true, priority: 0 });
      await loadRateLimitRules();
      await loadStats();
    } catch (err) { console.error('Create rule error:', err); }
  };

  const handleToggleRateLimit = async (id: number) => {
    await toggleRateLimitRule(id);
    await loadRateLimitRules();
    await loadStats();
  };

  const handleDeleteRateLimit = async (id: number) => {
    if (!window.confirm('Bu kuralı silmek istediğinize emin misiniz?')) return;
    await deleteRateLimitRule(id);
    await loadRateLimitRules();
    await loadStats();
  };

  // ════════════════════════════════════════════════════════════════════════
  // IP Access CRUD
  // ════════════════════════════════════════════════════════════════════════

  const handleCreateIpRule = async () => {
    try {
      await createIpAccessRule({
        ...newIpRule,
        ipRangeEnd: newIpRule.ipRangeEnd || null,
        companyId: newIpRule.companyId || null,
        reason: newIpRule.reason || null,
        expiresAt: newIpRule.expiresAt || null,
      });
      setShowIpDialog(false);
      setNewIpRule({ ipAddress: '', ipRangeEnd: '', ruleType: 'BLACKLIST', scope: 'GLOBAL', companyId: '', reason: '', isActive: true, isPermanent: false, expiresAt: null });
      await loadIpAccessRules();
      await loadStats();
    } catch (err) { console.error('Create IP rule error:', err); }
  };

  const handleToggleIpRule = async (id: number) => {
    await toggleIpAccessRule(id);
    await loadIpAccessRules();
    await loadStats();
  };

  const handleDeleteIpRule = async (id: number) => {
    if (!window.confirm('Bu kuralı silmek istediğinize emin misiniz?')) return;
    await deleteIpAccessRule(id);
    await loadIpAccessRules();
    await loadStats();
  };

  // ════════════════════════════════════════════════════════════════════════
  // Render
  // ════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <SuperAdminLayout title="API Güvenlik Monitörü" icon={<ShieldIcon />}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout title="API Güvenlik Monitörü" icon={<ShieldIcon />}>
      <Box sx={{ minWidth: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <Button startIcon={<RefreshIcon />} variant="outlined" onClick={loadAll}>Yenile</Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard elevation={2}>
              <SecurityIcon sx={{ fontSize: 28, color: '#ff9800', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">{stats?.rateLimitCount ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">Rate Limit</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard elevation={2}>
              <BlockIcon sx={{ fontSize: 28, color: '#f44336', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">{stats?.ipBlockedCount ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">IP Engel</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard elevation={2}>
              <WarningIcon sx={{ fontSize: 28, color: '#9c27b0', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">{stats?.authFailedCount ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">Auth Hata</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard elevation={2}>
              <ErrorIcon sx={{ fontSize: 28, color: '#d32f2f', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">{stats?.exceptionCount ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">Exception</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard elevation={2}>
              <SecurityIcon sx={{ fontSize: 28, color: '#2196f3', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">{stats?.activeRateLimitRules ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">Rate Limit Kuralı</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard elevation={2}>
              <BlockIcon sx={{ fontSize: 28, color: '#4caf50', mb: 1 }} />
              <Typography variant="h5" fontWeight="bold">{stats?.activeIpRules ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">IP Kuralı</Typography>
            </StatCard>
          </Grid>
        </Grid>

        {/* Top IPs & Endpoints */}
        {stats && ((stats.topBlockedIps?.length ?? 0) > 0 || (stats.topErrorEndpoints?.length ?? 0) > 0) && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {(stats.topBlockedIps?.length ?? 0) > 0 && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>En Çok Engellenen IP'ler (24s)</Typography>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>IP</TableCell><TableCell align="right">Sayı</TableCell></TableRow></TableHead>
                    <TableBody>
                      {(stats.topBlockedIps || []).map((ip, i) => (
                        <TableRow key={i}><TableCell><code>{ip.ip}</code></TableCell><TableCell align="right"><strong>{ip.count}</strong></TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            )}
            {(stats.topErrorEndpoints?.length ?? 0) > 0 && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>En Çok Hata Veren Endpoint'ler (24s)</Typography>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Path</TableCell><TableCell align="right">Sayı</TableCell></TableRow></TableHead>
                    <TableBody>
                      {(stats.topErrorEndpoints || []).map((ep, i) => (
                        <TableRow key={i}><TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}><code>{ep.path}</code></TableCell><TableCell align="right"><strong>{ep.count}</strong></TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth">
            <Tab label="Rate Limit Kuralları" />
            <Tab label="IP Erişim Kuralları" />
            <Tab label="Güvenlik Logları" />
            <Tab label="Canlı İzleme" />
            <Tab label="Dosya Logları" />
            <Tab label="JWT Devreye Alma" />
          </Tabs>
        </Paper>

        {/* Tab 0: Rate Limit Rules */}
        {activeTab === 0 && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Rate Limit Kuralları</Typography>
              <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setShowRateLimitDialog(true)}>Yeni Kural</Button>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Kural Adı</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell>Şirket</TableCell>
                    <TableCell>Endpoint</TableCell>
                    <TableCell align="center">Max İstek</TableCell>
                    <TableCell align="center">Pencere (sn)</TableCell>
                    <TableCell align="center">Burst</TableCell>
                    <TableCell align="center">Öncelik</TableCell>
                    <TableCell align="center">Aktif</TableCell>
                    <TableCell align="center">İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rateLimitRules.map(rule => (
                    <TableRow key={rule.id}>
                      <TableCell><strong>{rule.ruleName}</strong></TableCell>
                      <TableCell><Chip label={rule.scope} size="small" color="primary" variant="outlined" /></TableCell>
                      <TableCell>{rule.companyId || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{rule.endpointPattern || '-'}</TableCell>
                      <TableCell align="center">{rule.maxRequests}</TableCell>
                      <TableCell align="center">{rule.windowSeconds}</TableCell>
                      <TableCell align="center">{rule.burstLimit ?? '-'}</TableCell>
                      <TableCell align="center">{rule.priority}</TableCell>
                      <TableCell align="center">
                        <Switch checked={rule.isActive} onChange={() => handleToggleRateLimit(rule.id)} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleDeleteRateLimit(rule.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rateLimitRules.length === 0 && (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>Henüz kural tanımlanmamış</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Tab 1: IP Access Rules */}
        {activeTab === 1 && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">IP Erişim Kuralları</Typography>
              <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setShowIpDialog(true)}>Yeni Kural</Button>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>IP Adresi</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell>Şirket</TableCell>
                    <TableCell>Sebep</TableCell>
                    <TableCell align="center">Kalıcı</TableCell>
                    <TableCell align="center">Engellenen</TableCell>
                    <TableCell align="center">Aktif</TableCell>
                    <TableCell align="center">İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ipAccessRules.map(rule => (
                    <TableRow key={rule.id}>
                      <TableCell><code>{rule.ipAddress}{rule.ipRangeEnd ? ` - ${rule.ipRangeEnd}` : ''}</code></TableCell>
                      <TableCell><Chip label={rule.ruleType} size="small" color={rule.ruleType === 'BLACKLIST' ? 'error' : 'success'} /></TableCell>
                      <TableCell>{rule.scope}</TableCell>
                      <TableCell>{rule.companyId || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{rule.reason || '-'}</TableCell>
                      <TableCell align="center">{rule.isPermanent ? 'Evet' : rule.expiresAt ? new Date(rule.expiresAt).toLocaleDateString('tr-TR') : '-'}</TableCell>
                      <TableCell align="center"><strong>{rule.blockedRequestCount}</strong></TableCell>
                      <TableCell align="center">
                        <Switch checked={rule.isActive} onChange={() => handleToggleIpRule(rule.id)} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleDeleteIpRule(rule.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {ipAccessRules.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>Henüz IP kuralı tanımlanmamış</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Tab 2: Security Logs */}
        {activeTab === 2 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Güvenlik Logları</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Log Tipi</InputLabel>
                <Select
                  value={logFilters.logType || ''}
                  label="Log Tipi"
                  onChange={e => setLogFilters(f => ({ ...f, logType: e.target.value || undefined, page: 1 }))}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="RATE_LIMIT">Rate Limit</MenuItem>
                  <MenuItem value="IP_BLOCKED">IP Engel</MenuItem>
                  <MenuItem value="AUTH_FAILED">Auth Hata</MenuItem>
                  <MenuItem value="EXCEPTION">Exception</MenuItem>
                  <MenuItem value="SUSPICIOUS">Şüpheli</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>API</InputLabel>
                <Select
                  value={logFilters.apiName || ''}
                  label="API"
                  onChange={e => setLogFilters(f => ({ ...f, apiName: e.target.value || undefined, page: 1 }))}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {apiNames.map(name => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" label="IP Filtre" value={logFilters.ip || ''} onChange={e => setLogFilters(f => ({ ...f, ip: e.target.value || undefined, page: 1 }))} sx={{ width: 160 }} />
              <TextField size="small" label="Şirket ID" value={logFilters.companyId || ''} onChange={e => setLogFilters(f => ({ ...f, companyId: e.target.value || undefined, page: 1 }))} sx={{ width: 160 }} />
              <Button variant="outlined" size="small" onClick={loadLogs} startIcon={<RefreshIcon />}>Filtrele</Button>
              <Button
                variant={logFilters.logType === 'EXCEPTION' ? 'contained' : 'outlined'}
                size="small"
                color="error"
                startIcon={<ErrorIcon />}
                onClick={() => setLogFilters(f => ({
                  ...f,
                  logType: f.logType === 'EXCEPTION' ? undefined : 'EXCEPTION',
                  page: 1
                }))}
              >
                Sadece Hatalar
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => setLogFilters({ page: 1, pageSize: 50 })}
              >
                Filtreleri Temizle
              </Button>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>Bir satıra tıklayarak <strong>StackTrace</strong> ve <strong>Request Body</strong> dahil tüm detayları görebilirsiniz.</Alert>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Tarih</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>Mesaj</TableCell>
                    <TableCell>API</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map(log => (
                    <TableRow
                      key={log.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        bgcolor: log.logType === 'EXCEPTION' ? 'rgba(244,67,54,0.05)' : 'inherit',
                        '&:hover': { bgcolor: log.logType === 'EXCEPTION' ? 'rgba(244,67,54,0.12)' : 'rgba(0,0,0,0.04)' }
                      }}
                      onClick={() => openLogDetail(log.id)}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{new Date(log.createdAt).toLocaleString('tr-TR')}</TableCell>
                      <TableCell><Chip label={LOG_TYPE_LABELS[log.logType] || log.logType} size="small" color={LOG_TYPE_COLORS[log.logType] || 'default'} /></TableCell>
                      <TableCell><code style={{ fontSize: '0.75rem' }}>{log.ipAddress || '-'}</code></TableCell>
                      <TableCell>{log.httpMethod || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem' }}>{log.requestPath || '-'}</TableCell>
                      <TableCell>{log.statusCode || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem' }}>
                        <Tooltip title={log.message || ''}><span>{log.message ? (log.message.length > 80 ? log.message.substring(0, 80) + '...' : log.message) : '-'}</span></Tooltip>
                      </TableCell>
                      <TableCell>{log.apiName || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {logs.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Log bulunamadı</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {logTotalCount > (logFilters.pageSize || 50) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={Math.ceil(logTotalCount / (logFilters.pageSize || 50))}
                  page={logFilters.page || 1}
                  onChange={(_, page) => setLogFilters(f => ({ ...f, page }))}
                  color="primary"
                />
              </Box>
            )}
          </Paper>
        )}

        {/* Tab 3: Live Monitoring */}
        {activeTab === 3 && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">Canlı İzleme</Typography>
                <Chip label="Otomatik Yenileme: 10sn" size="small" color="success" variant="outlined" />
              </Box>
              <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={loadRecentLogs}>Yenile</Button>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>Son 50 güvenlik logu gösteriliyor. Sayfa açık kaldığı sürece 10 saniyede bir otomatik yenilenir.</Alert>
            <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Tarih</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>Mesaj</TableCell>
                    <TableCell>Süre (ms)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentLogs.map(log => (
                    <TableRow
                      key={log.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        bgcolor: log.logType === 'EXCEPTION' ? 'rgba(244,67,54,0.05)' : log.logType === 'IP_BLOCKED' ? 'rgba(244,67,54,0.03)' : 'inherit'
                      }}
                      onClick={() => openLogDetail(log.id)}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{new Date(log.createdAt).toLocaleString('tr-TR')}</TableCell>
                      <TableCell><Chip label={LOG_TYPE_LABELS[log.logType] || log.logType} size="small" color={LOG_TYPE_COLORS[log.logType] || 'default'} /></TableCell>
                      <TableCell><code style={{ fontSize: '0.75rem' }}>{log.ipAddress || '-'}</code></TableCell>
                      <TableCell>{log.httpMethod || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem' }}>{log.requestPath || '-'}</TableCell>
                      <TableCell>{log.statusCode || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem' }}>
                        <Tooltip title={log.message || ''}><span>{log.message ? (log.message.length > 60 ? log.message.substring(0, 60) + '...' : log.message) : '-'}</span></Tooltip>
                      </TableCell>
                      <TableCell>{log.requestDurationMs ?? '-'}</TableCell>
                    </TableRow>
                  ))}
                  {recentLogs.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Henüz log kaydı yok</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Tab 4: File Logs */}
        {activeTab === 4 && (
          <Paper sx={{ p: 2, overflow: 'hidden', minWidth: 0, width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">Dosya Logları</Typography>
                {fileLogsAutoRefresh && (
                  <Chip label="Auto-refresh: 5sn" size="small" color="success" variant="outlined" />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                  size="small"
                  onClick={loadFileLogsTail}
                  disabled={fileLogsLoading}
                >
                  {fileLogsLoading ? 'Yükleniyor...' : 'Oku'}
                </Button>
                <Button
                  variant={fileLogsAutoRefresh ? 'contained' : 'outlined'}
                  size="small"
                  color={fileLogsAutoRefresh ? 'success' : 'primary'}
                  onClick={() => setFileLogsAutoRefresh(!fileLogsAutoRefresh)}
                >
                  {fileLogsAutoRefresh ? 'Auto-Refresh: AÇIK' : 'Auto-Refresh: KAPALI'}
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Sunucudaki <code>C:\APIs\&lt;api&gt;\instance_*\logs\*.log</code> dosyalarını okur. Tüm uygulama log'ları
              (Info, Warning, Error, Debug) bu dosyalarda. DB log'undan daha detaylıdır.
            </Alert>

            {/* Filtreler */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>API</InputLabel>
                <Select
                  value={fileLogsApi}
                  label="API"
                  onChange={e => { setFileLogsApi(e.target.value); setFileLogsSelectedFile(''); }}
                >
                  {apiNames.map(name => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Instance</InputLabel>
                <Select
                  value={fileLogsInstance}
                  label="Instance"
                  onChange={e => { setFileLogsInstance(e.target.value as 'instance_a' | 'instance_b'); setFileLogsSelectedFile(''); }}
                >
                  <MenuItem value="instance_a">instance_a</MenuItem>
                  <MenuItem value="instance_b">instance_b</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel>Dosya</InputLabel>
                <Select
                  value={fileLogsSelectedFile}
                  label="Dosya"
                  onChange={e => setFileLogsSelectedFile(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">En son (otomatik)</MenuItem>
                  {fileLogsList.map(f => (
                    <MenuItem key={f.fileName} value={f.fileName}>
                      {f.fileName} ({f.sizeKB} KB)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Satır"
                type="number"
                value={fileLogsLines}
                onChange={e => setFileLogsLines(Math.max(1, Math.min(50000, Number(e.target.value) || 200)))}
                sx={{ width: 100 }}
              />
              <TextField
                size="small"
                label="Filtre (grep)"
                value={fileLogsGrep}
                onChange={e => setFileLogsGrep(e.target.value)}
                placeholder="ERR, ExceptionType vs."
                sx={{ width: 200 }}
              />
            </Box>

            {/* Hızlı butonlar */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" color="error" onClick={() => { setFileLogsGrep('[ERR]'); }}>
                [ERR] Hatalar
              </Button>
              <Button size="small" variant="outlined" color="warning" onClick={() => { setFileLogsGrep('[WRN]'); }}>
                [WRN] Uyarılar
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={() => { setFileLogsGrep('Exception'); }}>
                Exception
              </Button>
              <Button size="small" variant="text" onClick={() => setFileLogsGrep('')}>
                Filtre Temizle
              </Button>
            </Box>

            {fileLogsError && (
              <Alert severity="warning" sx={{ mb: 2 }}>{fileLogsError}</Alert>
            )}

            {/* Log içerik araç çubuğu */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {fileLogsContent.length > 0 && `${fileLogsContent.length} satır`}
                {fileLogsList.length > 0 && ` · ${fileLogsList.length} log dosyası`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={fileLogsWrap ? 'Satır kaydırma KAPALI' : 'Satır kaydırma AÇIK'}>
                  <IconButton
                    size="small"
                    color={fileLogsWrap ? 'primary' : 'default'}
                    onClick={() => setFileLogsWrap(!fileLogsWrap)}
                  >
                    <WrapTextIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="İçeriği kopyala">
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(fileLogsContent.join('\n'))}
                    disabled={fileLogsContent.length === 0}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Tam Ekran">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setFileLogsFullscreen(true)}
                    disabled={fileLogsContent.length === 0}
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Log içeriği - terminal görünümü (normal modda) */}
            <LogViewer
              lines={fileLogsContent}
              loading={fileLogsLoading}
              wrap={fileLogsWrap}
              maxHeight={600}
            />
          </Paper>
        )}

        {/* Tab 5: JWT Devreye Alma İzlemesi */}
        {activeTab === 5 && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">JWT Devreye Alma — 401 (AUTH_FAILED) Özeti</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Pencere</InputLabel>
                  <Select
                    value={authFailedHours}
                    label="Pencere"
                    onChange={e => setAuthFailedHours(Number(e.target.value))}
                  >
                    <MenuItem value={1}>Son 1 saat</MenuItem>
                    <MenuItem value={6}>Son 6 saat</MenuItem>
                    <MenuItem value={24}>Son 24 saat</MenuItem>
                    <MenuItem value={168}>Son 7 gün</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                  size="small"
                  onClick={loadAuthFailedSummary}
                  disabled={authFailedLoading}
                >
                  {authFailedLoading ? 'Yükleniyor...' : 'Yenile'}
                </Button>
                <Button
                  variant={authFailedAutoRefresh ? 'contained' : 'outlined'}
                  size="small"
                  color={authFailedAutoRefresh ? 'success' : 'primary'}
                  onClick={() => setAuthFailedAutoRefresh(!authFailedAutoRefresh)}
                >
                  {authFailedAutoRefresh ? 'Otomatik: AÇIK (30sn)' : 'Otomatik: KAPALI'}
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <code>OwnJwt:Enabled</code> bayrağı açılan servislerde JWT olmadan gelen her istek burada görünür.
              <strong> Her kayıt hata değil</strong> — gerçek yetkisiz erişim girişimleri de aynı tabloya düşüyor.
              Ayrım: <strong>İstek/IP oranı yüksek + tanıdık User-Agent</strong> ise bizim çağrımız (muafiyet eksik ya da
              çağıran token göndermiyor, önce çağıranı bulun). Çok IP'ye dağılmış tek tek kayıtlar taramadır, dokunmayın.
            </Alert>

            {authFailedLoading && !authFailedSummary && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            )}

            {authFailedSummary && (
              <>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip
                    label={`Toplam ${authFailedSummary.totalCount} kayıt`}
                    color={authFailedSummary.totalCount === 0 ? 'success' : 'secondary'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(authFailedSummary.since).toLocaleString('tr-TR')} tarihinden itibaren
                  </Typography>
                </Box>

                {authFailedSummary.totalCount === 0 && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Bu pencerede hiç 401 yok. Bayrağı açılan servislerde takılan uç bulunmuyor.
                  </Alert>
                )}

                {authFailedSummary.byApi.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Servis Bazında</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>API</TableCell>
                            <TableCell align="right">401 Sayısı</TableCell>
                            <TableCell align="right">Farklı Yol</TableCell>
                            <TableCell align="right">Farklı IP</TableCell>
                            <TableCell>Son Kayıt</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {authFailedSummary.byApi.map(row => (
                            <TableRow key={row.apiName}>
                              <TableCell><strong>{row.apiName}</strong></TableCell>
                              <TableCell align="right"><strong>{row.count}</strong></TableCell>
                              <TableCell align="right">{row.distinctPaths}</TableCell>
                              <TableCell align="right">{row.distinctIps}</TableCell>
                              <TableCell>{new Date(row.lastSeen).toLocaleString('tr-TR')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {authFailedSummary.byPath.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Yol Bazında</Typography>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 1000 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>API</TableCell>
                            <TableCell>Metod</TableCell>
                            <TableCell>Yol</TableCell>
                            <TableCell align="right">Sayı</TableCell>
                            <TableCell align="right">Farklı IP</TableCell>
                            <TableCell align="right">
                              <Tooltip title="İstek / farklı IP oranı. Yüksekse tek istemci ısrarla deniyor: muhtemelen bizim çağrımız.">
                                <span>İstek/IP</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>Örnek User-Agent</TableCell>
                            <TableCell>Son Kayıt</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {authFailedSummary.byPath.map((row, i) => (
                            <TableRow key={i}>
                              <TableCell>{row.apiName}</TableCell>
                              <TableCell><Chip label={row.httpMethod || '-'} size="small" variant="outlined" /></TableCell>
                              <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <Tooltip title={row.path}><code>{row.path}</code></Tooltip>
                              </TableCell>
                              <TableCell align="right"><strong>{row.count}</strong></TableCell>
                              <TableCell align="right">{row.distinctIps}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={row.requestsPerIp}
                                  size="small"
                                  color={row.requestsPerIp >= 5 ? 'warning' : 'default'}
                                />
                              </TableCell>
                              <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <Tooltip title={row.sampleUserAgent || '-'}>
                                  <span>{row.sampleUserAgent || '-'}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell>{new Date(row.lastSeen).toLocaleString('tr-TR')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </>
            )}
          </Paper>
        )}

        {/* Tam Ekran Log Görüntüleyici */}
        <Dialog
          open={fileLogsFullscreen}
          onClose={() => setFileLogsFullscreen(false)}
          fullScreen
          PaperProps={{ sx: { bgcolor: '#1e1e1e' } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: '#252526',
              color: 'white',
              borderBottom: '1px solid #444',
              py: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem' }}>
              <Typography variant="subtitle1" component="span" sx={{ color: '#90caf9' }}>
                {fileLogsApi}
              </Typography>
              <Typography variant="caption" component="span" sx={{ color: '#aaa' }}>
                · {fileLogsInstance}
              </Typography>
              {fileLogsSelectedFile && (
                <Typography variant="caption" component="span" sx={{ color: '#aaa' }}>
                  · {fileLogsSelectedFile}
                </Typography>
              )}
              <Chip
                label={`${fileLogsContent.length} satır`}
                size="small"
                sx={{ bgcolor: '#333', color: '#e0e0e0', ml: 1 }}
              />
              {fileLogsAutoRefresh && (
                <Chip label="LIVE" size="small" color="success" sx={{ ml: 0.5 }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={fileLogsWrap ? 'Satır kaydırma KAPALI' : 'Satır kaydırma AÇIK'}>
                <IconButton size="small" sx={{ color: 'white' }} onClick={() => setFileLogsWrap(!fileLogsWrap)}>
                  <WrapTextIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="İçeriği kopyala">
                <IconButton
                  size="small"
                  sx={{ color: 'white' }}
                  onClick={() => navigator.clipboard.writeText(fileLogsContent.join('\n'))}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Yenile">
                <IconButton size="small" sx={{ color: 'white' }} onClick={loadFileLogsTail} disabled={fileLogsLoading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Auto-Refresh">
                <IconButton
                  size="small"
                  sx={{ color: fileLogsAutoRefresh ? '#4caf50' : 'white' }}
                  onClick={() => setFileLogsAutoRefresh(!fileLogsAutoRefresh)}
                >
                  <SyncIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Kapat (ESC)">
                <IconButton size="small" sx={{ color: 'white' }} onClick={() => setFileLogsFullscreen(false)}>
                  <FullscreenExitIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, bgcolor: '#1e1e1e' }}>
            <LogViewer
              lines={fileLogsContent}
              loading={fileLogsLoading}
              wrap={fileLogsWrap}
              maxHeight="calc(100vh - 64px)"
              fullscreen
            />
          </DialogContent>
        </Dialog>

        {/* Log Detail Modal */}
        <Dialog open={logDetailOpen} onClose={() => setLogDetailOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon color={logDetail?.logType === 'EXCEPTION' ? 'error' : 'primary'} />
            Log Detayı
            {logDetail && (
              <Chip
                label={LOG_TYPE_LABELS[logDetail.logType] || logDetail.logType}
                size="small"
                color={LOG_TYPE_COLORS[logDetail.logType] || 'default'}
                sx={{ ml: 1 }}
              />
            )}
            {logDetail && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>#{logDetail.id}</Typography>}
          </DialogTitle>
          <DialogContent dividers>
            {logDetailLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {!logDetailLoading && logDetail && (
              <Grid container spacing={2}>
                {/* Üst kısım - özet bilgi */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Tarih</Typography>
                  <Typography variant="body2"><strong>{new Date(logDetail.createdAt).toLocaleString('tr-TR')}</strong></Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">API</Typography>
                  <Typography variant="body2"><strong>{logDetail.apiName || '(unknown)'}</strong></Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">IP Adresi</Typography>
                  <Typography variant="body2"><code>{logDetail.ipAddress || '-'}</code></Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Şirket ID</Typography>
                  <Typography variant="body2">{logDetail.companyId || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">HTTP Method + Status</Typography>
                  <Typography variant="body2">
                    <Chip label={logDetail.httpMethod || '-'} size="small" sx={{ mr: 1 }} />
                    <Chip
                      label={logDetail.statusCode ?? '-'}
                      size="small"
                      color={(logDetail.statusCode ?? 0) >= 500 ? 'error' : (logDetail.statusCode ?? 0) >= 400 ? 'warning' : 'success'}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Süre</Typography>
                  <Typography variant="body2">{logDetail.requestDurationMs ?? '-'} ms</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Request Path</Typography>
                  <Typography variant="body2"><code style={{ wordBreak: 'break-all' }}>{logDetail.requestPath || '-'}</code></Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Mesaj</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{logDetail.message || '-'}</Typography>
                </Grid>

                {/* Exception alanları */}
                {logDetail.exceptionType && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Exception Tipi</Typography>
                    <Typography variant="body2"><strong>{logDetail.exceptionType}</strong></Typography>
                  </Grid>
                )}
                {logDetail.exceptionMessage && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Exception Mesajı</Typography>
                    <Box
                      sx={{
                        bgcolor: '#fff3e0',
                        p: 1.5,
                        borderRadius: 1,
                        fontFamily: 'Consolas, monospace',
                        fontSize: '0.8rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        border: '1px solid #ffb74d'
                      }}
                    >
                      {logDetail.exceptionMessage}
                    </Box>
                  </Grid>
                )}
                {logDetail.stackTrace && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Stack Trace</Typography>
                    <Box
                      sx={{
                        bgcolor: '#1e1e1e',
                        color: '#ff6b6b',
                        p: 1.5,
                        borderRadius: 1,
                        fontFamily: 'Consolas, "Courier New", monospace',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre',
                        overflow: 'auto',
                        maxHeight: 400,
                        border: '1px solid #444'
                      }}
                    >
                      {logDetail.stackTrace}
                    </Box>
                  </Grid>
                )}
                {logDetail.requestBody && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Request Body</Typography>
                    <Box
                      sx={{
                        bgcolor: '#f5f5f5',
                        p: 1.5,
                        borderRadius: 1,
                        fontFamily: 'Consolas, monospace',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 300,
                        overflow: 'auto',
                        border: '1px solid #ddd'
                      }}
                    >
                      {logDetail.requestBody}
                    </Box>
                  </Grid>
                )}
                {logDetail.userAgent && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">User Agent</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>{logDetail.userAgent}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
            {!logDetailLoading && !logDetail && (
              <Alert severity="warning">Log detayı yüklenemedi</Alert>
            )}
          </DialogContent>
          <DialogActions>
            {logDetail && (logDetail.stackTrace || logDetail.exceptionMessage) && (
              <Button
                onClick={() => {
                  const text = [
                    `Log #${logDetail.id} - ${logDetail.logType}`,
                    `Tarih: ${new Date(logDetail.createdAt).toLocaleString('tr-TR')}`,
                    `API: ${logDetail.apiName || '-'}`,
                    `Path: ${logDetail.httpMethod} ${logDetail.requestPath}`,
                    `Exception: ${logDetail.exceptionType || '-'}`,
                    `Message: ${logDetail.exceptionMessage || logDetail.message || '-'}`,
                    '',
                    'Stack Trace:',
                    logDetail.stackTrace || '(yok)'
                  ].join('\n');
                  navigator.clipboard.writeText(text);
                }}
              >
                Kopyala
              </Button>
            )}
            <Button onClick={() => setLogDetailOpen(false)} variant="contained">Kapat</Button>
          </DialogActions>
        </Dialog>

        {/* Rate Limit Rule Dialog */}
        <Dialog open={showRateLimitDialog} onClose={() => setShowRateLimitDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Yeni Rate Limit Kuralı</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
            <TextField label="Kural Adı" value={newRateLimitRule.ruleName} onChange={e => setNewRateLimitRule(r => ({ ...r, ruleName: e.target.value }))} fullWidth required />
            <FormControl fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select value={newRateLimitRule.scope} label="Scope" onChange={e => setNewRateLimitRule(r => ({ ...r, scope: e.target.value }))}>
                <MenuItem value="GLOBAL">GLOBAL</MenuItem>
                <MenuItem value="COMPANY">COMPANY</MenuItem>
                <MenuItem value="IP">IP</MenuItem>
                <MenuItem value="ENDPOINT">ENDPOINT</MenuItem>
              </Select>
            </FormControl>
            {newRateLimitRule.scope === 'COMPANY' && (
              <TextField label="Şirket ID" value={newRateLimitRule.companyId} onChange={e => setNewRateLimitRule(r => ({ ...r, companyId: e.target.value }))} fullWidth />
            )}
            {newRateLimitRule.scope === 'ENDPOINT' && (
              <TextField label="Endpoint Pattern" value={newRateLimitRule.endpointPattern} onChange={e => setNewRateLimitRule(r => ({ ...r, endpointPattern: e.target.value }))} fullWidth placeholder="/api/auth/*" />
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Max İstek" type="number" value={newRateLimitRule.maxRequests} onChange={e => setNewRateLimitRule(r => ({ ...r, maxRequests: Number(e.target.value) }))} fullWidth />
              <TextField label="Pencere (sn)" type="number" value={newRateLimitRule.windowSeconds} onChange={e => setNewRateLimitRule(r => ({ ...r, windowSeconds: Number(e.target.value) }))} fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Burst Limit" type="number" value={newRateLimitRule.burstLimit} onChange={e => setNewRateLimitRule(r => ({ ...r, burstLimit: Number(e.target.value) }))} fullWidth />
              <TextField label="Öncelik" type="number" value={newRateLimitRule.priority} onChange={e => setNewRateLimitRule(r => ({ ...r, priority: Number(e.target.value) }))} fullWidth />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRateLimitDialog(false)}>İptal</Button>
            <Button variant="contained" onClick={handleCreateRateLimitRule} disabled={!newRateLimitRule.ruleName}>Oluştur</Button>
          </DialogActions>
        </Dialog>

        {/* IP Access Rule Dialog */}
        <Dialog open={showIpDialog} onClose={() => setShowIpDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Yeni IP Kuralı</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
            <TextField label="IP Adresi" value={newIpRule.ipAddress} onChange={e => setNewIpRule(r => ({ ...r, ipAddress: e.target.value }))} fullWidth required placeholder="192.168.1.1" />
            <TextField label="IP Aralık Sonu (opsiyonel)" value={newIpRule.ipRangeEnd} onChange={e => setNewIpRule(r => ({ ...r, ipRangeEnd: e.target.value }))} fullWidth placeholder="192.168.1.255" />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Tip</InputLabel>
                <Select value={newIpRule.ruleType} label="Tip" onChange={e => setNewIpRule(r => ({ ...r, ruleType: e.target.value }))}>
                  <MenuItem value="BLACKLIST">Kara Liste</MenuItem>
                  <MenuItem value="WHITELIST">Beyaz Liste</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select value={newIpRule.scope} label="Scope" onChange={e => setNewIpRule(r => ({ ...r, scope: e.target.value }))}>
                  <MenuItem value="GLOBAL">GLOBAL</MenuItem>
                  <MenuItem value="COMPANY">COMPANY</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {newIpRule.scope === 'COMPANY' && (
              <TextField label="Şirket ID" value={newIpRule.companyId} onChange={e => setNewIpRule(r => ({ ...r, companyId: e.target.value }))} fullWidth />
            )}
            <TextField label="Sebep" value={newIpRule.reason} onChange={e => setNewIpRule(r => ({ ...r, reason: e.target.value }))} fullWidth multiline rows={2} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowIpDialog(false)}>İptal</Button>
            <Button variant="contained" onClick={handleCreateIpRule} disabled={!newIpRule.ipAddress}>Oluştur</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SuperAdminLayout>
  );
};

export default MiddlewareMonitor;
